import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "../ui/Dialog.js";
import { ApiError } from "../api.js";
import type { DiaryPaymentClassification } from "../booking/diaryPayments.js";
import { diaryPaymentStateLabel } from "../booking/diaryPayments.js";
import { formatDay, formatTime } from "../format.js";
import type { DiaryEntry } from "./entries.js";
import { dayOf } from "./grid.js";
import { updateLessonAttendance } from "./lessonAttendance.js";

type NamedEntry = Extract<DiaryEntry, { anonymous: false }>;

export interface TeachingDayAppointment {
  entry: NamedEntry;
  canMarkPresent: boolean;
  attendanceLabel: string;
}

function isNamedEntry(entry: DiaryEntry): entry is NamedEntry {
  return entry.anonymous === false;
}

function isNoShow(entry: NamedEntry): boolean {
  const bookingStatus = entry.bookingStatus.toLowerCase();
  const appointmentStatus = entry.status.toLowerCase();
  const attendance = (entry.attendanceStatus ?? "").toLowerCase();
  return (
    bookingStatus === "no-show" ||
    bookingStatus === "no_show" ||
    bookingStatus === "noshow" ||
    appointmentStatus === "no-show" ||
    appointmentStatus === "no_show" ||
    appointmentStatus === "noshow" ||
    attendance === "absent"
  );
}

function isCancelled(entry: NamedEntry): boolean {
  return ["canceled", "cancelled"].includes(entry.bookingStatus.toLowerCase()) ||
    ["canceled", "cancelled"].includes(entry.status.toLowerCase()) ||
    (entry.attendanceStatus ?? "").toLowerCase() === "cancelled";
}

function canMarkAttendance(entry: NamedEntry, now: number): boolean {
  const startsAt = Date.parse(entry.startsAt);
  const endsAt = Date.parse(entry.endsAt);
  return (
    entry.bookingId.trim() !== "" &&
    entry.studentId !== null &&
    Number.isFinite(startsAt) &&
    Number.isFinite(endsAt) &&
    endsAt > startsAt &&
    !entry.isSchools &&
    startsAt <= now &&
    entry.status.toLowerCase() === "approved" &&
    entry.bookingStatus.toLowerCase() === "approved" &&
    !isNoShow(entry) &&
    !isCancelled(entry) &&
    (entry.attendanceStatus === null || entry.attendanceStatus.trim() === "")
  );
}

/** Named appointments are already authorised by the diary API; anonymous busy slots never enter this list. */
export function teachingDayAppointments(
  entries: readonly DiaryEntry[],
  tutorId: string | null,
  day: string,
  now: number = Date.now(),
): TeachingDayAppointment[] {
  if (tutorId === null) return [];
  return entries
    .filter(isNamedEntry)
    .filter((entry) => entry.tutorId === tutorId && dayOf(entry.startsAt) === day)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.bookingId.localeCompare(b.bookingId))
    .map((entry) => {
      const attendance = (entry.attendanceStatus ?? "").toLowerCase();
      let attendanceLabel = "Attendance not recorded";
      if (isNoShow(entry)) attendanceLabel = "No-show";
      else if (isCancelled(entry)) attendanceLabel = "Cancelled";
      else if (attendance === "present") attendanceLabel = "Present";
      else if (attendance === "late") attendanceLabel = "Late";
      else if (Date.parse(entry.startsAt) > now) attendanceLabel = "Upcoming";
      else if (entry.isSchools) attendanceLabel = "Use the School lesson workspace";
      else if (!canMarkAttendance(entry, now)) attendanceLabel = "Not eligible";

      return {
        entry,
        canMarkPresent: canMarkAttendance(entry, now),
        attendanceLabel,
      };
    });
}

function durationLabel(entry: NamedEntry): string {
  const duration = Date.parse(entry.endsAt) - Date.parse(entry.startsAt);
  if (!Number.isFinite(duration) || duration <= 0) return "Duration unavailable";
  const minutes = Math.round(duration / 60_000);
  if (minutes > 0 && minutes % 60 === 0) return `${minutes / 60} hr${minutes === 60 ? "" : "s"}`;
  return `${minutes} min`;
}

function noteStatus(entry: NamedEntry): string {
  if (entry.lessonId === null) return "No lesson note";
  return entry.lessonPublishedAt === null ? "Draft note" : "Published note";
}

function paymentStatus(
  entry: NamedEntry,
  payments: ReadonlyMap<number, DiaryPaymentClassification>,
  now: number,
): string {
  if (entry.isSchools) return "School payment status unavailable here";
  if (entry.externalId === null) return "Payment status unavailable";
  const payment = payments.get(entry.externalId);
  if (!payment || payment.status === "unknown") return "Payment status unavailable";
  return diaryPaymentStateLabel(payment, entry.endsAt, new Date(now)) ?? "No payment required";
}

type Result = {
  state: "saved" | "retryable" | "blocked";
  message: string;
};

function failureResult(cause: unknown): Result {
  if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) {
    return {
      state: "blocked",
      message: "This lesson was not changed. Refresh the diary and check its status.",
    };
  }
  return {
    state: "retryable",
    message: "We couldn't confirm this write. Retrying sends the same attendance value.",
  };
}

export function TeachingDay({
  day,
  entries,
  payments,
  tutorId,
  onMarked,
}: {
  day: string;
  entries: readonly DiaryEntry[];
  payments: ReadonlyMap<number, DiaryPaymentClassification>;
  tutorId: string;
  onMarked: () => void;
}) {
  const [now, setNow] = useState(Date.now);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [results, setResults] = useState<Map<string, Result>>(() => new Map());
  const [savingIds, setSavingIds] = useState<Set<string>>(() => new Set());
  const [summary, setSummary] = useState("");
  const [confirmingRows, setConfirmingRows] = useState<TeachingDayAppointment[] | null>(null);
  const busy = useRef(false);
  const scope = `${tutorId}:${day}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setResults(new Map());
    setSavingIds(new Set());
    setSummary("");
    setConfirmingRows(null);
  }, [day, tutorId]);

  const rows = useMemo(
    () => teachingDayAppointments(entries, tutorId, day, now),
    [day, entries, now, tutorId],
  );
  const selectable = rows.filter((row) =>
    row.canMarkPresent && !results.has(row.entry.bookingId),
  );
  const selectedRows = selectable.filter((row) => selected.has(row.entry.bookingId));
  const selectedIds = selectedRows.map((row) => row.entry.bookingId);
  const retryableIds = rows
    .filter((row) => row.canMarkPresent && results.get(row.entry.bookingId)?.state === "retryable")
    .map((row) => row.entry.bookingId);
  const allSelected = selectable.length > 0 &&
    selectable.every((row) => selected.has(row.entry.bookingId));
  const canShowAttendanceActions = selectable.length > 0 || retryableIds.length > 0;

  async function saveAttendance(bookingIds: readonly string[]) {
    const ids = [...new Set(bookingIds)];
    if (busy.current || ids.length === 0) return;
    busy.current = true;
    const requestScope = scope;
    setSavingIds(new Set(ids));
    setSummary("");

    const outcomes = await Promise.all(ids.map(async (bookingId): Promise<[string, Result]> => {
      try {
        const result = await updateLessonAttendance(bookingId, "present");
        if (result.attendanceStatus !== "present" || !result.lessonId) {
          return [bookingId, {
            state: "retryable",
            message: "We couldn't confirm this write. Retrying sends the same attendance value.",
          }];
        }
        return [bookingId, { state: "saved", message: "Attendance marked present." }];
      } catch (cause: unknown) {
        return [bookingId, failureResult(cause)];
      }
    }));

    const succeeded = outcomes.filter(([, result]) => result.state === "saved").length;
    const retryable = outcomes.filter(([, result]) => result.state === "retryable").length;
    const blocked = outcomes.filter(([, result]) => result.state === "blocked").length;
    if (currentScope.current === requestScope) {
      setResults((current) => {
        const next = new Map(current);
        for (const [bookingId, result] of outcomes) next.set(bookingId, result);
        return next;
      });
      setSelected((current) => {
        const next = new Set(current);
        for (const [bookingId, result] of outcomes) {
          if (result.state !== "retryable") next.delete(bookingId);
          else next.add(bookingId);
        }
        return next;
      });
      if (succeeded === ids.length) {
        setSummary(`Attendance marked for ${succeeded} lesson${succeeded === 1 ? "" : "s"}.`);
      } else if (succeeded > 0) {
        setSummary(`Attendance marked for ${succeeded} of ${ids.length} lessons. Review the remaining lesson outcomes below.`);
      } else if (retryable > 0) {
        setSummary(`Attendance could not be confirmed for ${retryable} lesson${retryable === 1 ? "" : "s"}. Retry only the listed failed lessons.`);
      } else if (blocked > 0) {
        setSummary("No lessons were marked. Refresh the diary and check the affected lesson statuses.");
      }
    }

    setSavingIds(new Set());
    busy.current = false;
    onMarked();
  }

  const headingId = `teaching-day-${day}`;
  const dateLabel = formatDay(`${day}T12:00:00.000Z`);

  return (
    <section className="panel" aria-labelledby={headingId}>
      <h2 id={headingId}>{dayOf(new Date(now).toISOString()) === day ? "My teaching day" : `My teaching · ${dateLabel}`}</h2>
      <p className="small muted">Chronological lessons for your tutor account.</p>
      {rows.length === 0 ? (
        <p className="empty">Nothing is booked for you on this day.</p>
      ) : (
        <>
          {canShowAttendanceActions && (
            <fieldset>
              <legend>Attendance</legend>
              <div className="row">
                <label>
                  <input
                    type="checkbox"
                    aria-label="Select all eligible lessons"
                    checked={allSelected}
                    disabled={savingIds.size > 0}
                    onChange={(event) => {
                      setSelected(event.currentTarget.checked
                        ? new Set(selectable.map((row) => row.entry.bookingId))
                        : new Set());
                    }}
                  />
                  Select all eligible lessons
                </label>
                <button
                  type="button"
                  disabled={savingIds.size > 0 || selectedIds.length === 0}
                  onClick={() => setConfirmingRows([...selectedRows])}
                >
                  Mark selected present
                </button>
                {retryableIds.length > 0 && (
                  <button
                    type="button"
                    className="action-secondary"
                    disabled={savingIds.size > 0}
                    onClick={() => void saveAttendance(retryableIds)}
                  >
                    Retry failed lessons
                  </button>
                )}
              </div>
            </fieldset>
          )}
          {summary && <p role="status" aria-live="polite">{summary}</p>}
          <ol aria-label="Lessons for your teaching day">
            {rows.map(({ entry, canMarkPresent, attendanceLabel }) => {
              const result = results.get(entry.bookingId);
              const isSaving = savingIds.has(entry.bookingId);
              const name = entry.studentName ?? entry.customerName ?? "Unknown student";
              const payment = paymentStatus(entry, payments, now);
              return (
                <li key={entry.bookingId} className="panel">
                  <div className="row">
                    {canMarkPresent && !result && (
                      <label>
                        <input
                          type="checkbox"
                          aria-label={`Select ${name} at ${formatTime(entry.startsAt)}`}
                          checked={selected.has(entry.bookingId)}
                          disabled={isSaving}
                          onChange={(event) => {
                            setSelected((current) => {
                              const next = new Set(current);
                              if (event.currentTarget.checked) next.add(entry.bookingId);
                              else next.delete(entry.bookingId);
                              return next;
                            });
                          }}
                        />
                        Select
                      </label>
                    )}
                    <time dateTime={entry.startsAt}>{formatTime(entry.startsAt)}</time>
                    <span>{durationLabel(entry)}</span>
                    <strong>{name}</strong>
                    {entry.isSchools && <span className="tag">School</span>}
                  </div>
                  <p className="small">{entry.serviceName ?? "Lesson"}</p>
                  <dl>
                    <div><dt>Attendance</dt><dd>{attendanceLabel}</dd></div>
                    <div><dt>Note</dt><dd>{noteStatus(entry)}</dd></div>
                    <div><dt>Payment</dt><dd>{payment}</dd></div>
                  </dl>
                  {entry.bookingId.trim() !== "" && (
                    <Link to={`/bookings/${encodeURIComponent(entry.bookingId)}/lesson`}>
                      {result?.state === "saved" ? "Change attendance" : "Open lesson workspace"}
                    </Link>
                  )}
                  {isSaving && <p role="status">Saving attendance…</p>}
                  {result && <p role="status">{result.message}</p>}
                </li>
              );
            })}
          </ol>
        </>
      )}
      {confirmingRows && (
        <ConfirmDialog
          title={`Mark ${confirmingRows.length} lessons present?`}
          description="Attendance changes lesson records and may affect tutor payability. Check each learner and time before confirming."
          confirmLabel={`Mark ${confirmingRows.length} present`}
          cancelLabel="Review selection"
          onClose={() => setConfirmingRows(null)}
          onConfirm={() => {
            const stillEligible = confirmingRows.every(({ entry }) =>
              rows.some((row) => row.entry.bookingId === entry.bookingId && row.canMarkPresent),
            );
            if (!stillEligible) {
              setConfirmingRows(null);
              setSummary("Some selected lessons have changed. Refresh the diary and review them before marking attendance.");
              onMarked();
              return;
            }
            const ids = confirmingRows.map(({ entry }) => entry.bookingId);
            setConfirmingRows(null);
            void saveAttendance(ids);
          }}
        >
          <ul aria-label="Lessons selected for attendance">
            {confirmingRows.map(({ entry }) => (
              <li key={entry.bookingId}>
                <strong>{entry.studentName ?? entry.customerName ?? "Unknown student"}</strong>
                {" · "}
                <time dateTime={entry.startsAt}>{formatTime(entry.startsAt)}</time>
              </li>
            ))}
          </ul>
        </ConfirmDialog>
      )}
    </section>
  );
}
